export const ADMIN_ROLES = [
  { id: 'sales', title: 'Sales Team', description: 'ติดตามร้านค้า ระบุผู้ขาย และรับค่าคอมมิชชันแพ็กเกจใน Wallet' },
  { id: 'editor', title: 'Editor', description: 'แก้ไขและลบ Content ตรวจคุณภาพรีวิว ตรวจยืนยัน Creator และดูผลงาน/Network' },
  { id: 'campaign', title: 'Marketing', description: 'จัดการ Merchant และร้านค้า สร้าง/แก้ไข/เปิด/พัก Campaign จัดการแพ็กเกจ และดู KPI Monitor' },
  { id: 'finance', title: 'Finance', description: 'จัดการเงิน แต้ม ราคา Reward งบ Campaign ฝาก/ถอน ยืนยันโอน Ledger และ Reconciliation' },
  { id: 'system', title: 'System', description: 'สิทธิ์สูงสุด ทำได้ทุกกลุ่ม จัดการ Admin และ Role ตั้งค่าระบบ KPI, Home blocks, Email/SMS และ Delivery Log' },
] as const;
export type AdminGroup = typeof ADMIN_ROLES[number]['id'];
export type AdminIdentity = { role?: string; account_roles?: string[]; admin_role?: string; admin_groups?: string[]; status?: string };

export function canAssignAdminRole(account: { status?: string; admin_stack?: { status?: string } }) {
  return (account.admin_stack?.status ?? account.status) === 'active';
}

export function adminGroups(account: AdminIdentity | null | undefined): AdminGroup[] {
  if (!account || (account.status && account.status !== 'active')) return [];
  if (Array.isArray(account.admin_groups)) return ADMIN_ROLES.map(role => role.id).filter(role => account.admin_groups!.includes(role));
  return [account.role, account.admin_role, ...(account.account_roles || [])].some(role => ['admin', 'super_admin'].includes(String(role).toLowerCase())) ? ['system'] : [];
}

export function canAdmin(account: AdminIdentity | null | undefined, ...groups: AdminGroup[]) {
  const assigned = adminGroups(account);
  return assigned.includes('system') || groups.some(group => assigned.includes(group));
}

export function canOpenAdminPath(account: AdminIdentity | null | undefined, pathname: string): boolean {
  const path = pathname.split('?')[0];
  if (!adminGroups(account).length) return false;
  if (path === '/presentation' || path === '/presentationslide') return true;
  if (path === '/admin/system/admins') return true;
  if (path === '/admin/system/kpi') return canAdmin(account, 'campaign');
  if (path.startsWith('/admin/creators')) return canAdmin(account, 'editor');
  if (path.startsWith('/admin/merchants')) return canAdmin(account, 'campaign');
  if (path.startsWith('/admin/places')) return canAdmin(account, 'campaign');
  if (path.startsWith('/admin/requests/campaign-credits')) return canAdmin(account, 'editor', 'finance');
  if (path.startsWith('/admin/requests/campaign-posts')) return canAdmin(account, 'campaign');
  if (path.startsWith('/admin/marketing/review-to-earn')) return canAdmin(account, 'campaign');
  if (path.startsWith('/admin/marketing/messages')) return canAdmin(account, 'campaign');
  if (path.startsWith('/admin/marketing/referral')) return canAdmin(account, 'campaign');
  if (path.startsWith('/admin/marketing/featured')) return canAdmin(account, 'campaign');
  if (path.startsWith('/admin/marketing/merchant-approaches')) return canAdmin(account, 'campaign', 'finance', 'sales');
  if (path.startsWith('/admin/marketing')) return canAdmin(account, 'campaign', 'finance');
  if (path.startsWith('/admin/requests') || path.startsWith('/admin/wallet')) return canAdmin(account, 'finance');
  if (path.startsWith('/admin/campaigns') || path === '/admin/system/campaign-packages') return canAdmin(account, 'campaign', 'finance');
  if (path.startsWith('/merchant/')) return canAdmin(account, 'campaign');
  return canAdmin(account, 'system');
}

export function adminHome(account: AdminIdentity | null | undefined): string {
  if (canAdmin(account, 'system')) return '/admin/system/admins';
  if (canAdmin(account, 'editor')) return '/admin/creators/verification';
  if (canAdmin(account, 'campaign')) return '/admin/campaigns';
  if (canAdmin(account, 'finance')) return '/admin/wallet/overview';
  if (canAdmin(account, 'sales')) return '/admin/marketing/merchant-approaches';
  return '/';
}


/** Shared destination after password, OTP, or completed profile setup. */
export function destinationAfterLogin(account: AdminIdentity | null | undefined): string {
  return adminGroups(account).length > 0 ? adminHome(account) : '/';
}
